import { createLogger } from '@entx/domain-primitives'
import { Form, useNavigation } from 'react-router'
import { auth } from '../../server/lib/auth-server'
import { isAuthenticationRateLimitFailure } from '../../server/lib/auth-abuse-observability'
import { VenueOnboardingShell } from '../components/venue-onboarding-shell'
import { Button, Field, Input } from '../components/ui'
import { VStack, styled } from '../styled-system/jsx'
import type { Route } from './+types/account-recovery'

const logger = createLogger('self-service.venue-operator-recovery')
const GENERIC_RECOVERY_MESSAGE = 'If an account can receive password recovery email, a message is on its way.'

export function meta() {
    return [{ title: 'Recover your venue operator account - ENTX' }]
}

export async function action({ request }: Route.ActionArgs) {
    const formData = await request.formData()
    const emailValue = formData.get('email')
    const email = typeof emailValue === 'string' ? emailValue.trim().toLowerCase() : ''

    if (email) {
        try {
            await auth.api.requestPasswordReset({
                headers: request.headers,
                body: { email, redirectTo: '/reset-password' },
            })
        } catch (error) {
            logger.warn('Venue operator password recovery request rejected', {
                errorType: error instanceof Error ? error.name : 'unknown',
                throttled: isAuthenticationRateLimitFailure(error),
            })
        }
    }

    return { message: GENERIC_RECOVERY_MESSAGE }
}

export default function AccountRecovery({ actionData }: Route.ComponentProps) {
    const navigation = useNavigation()

    return (
        <VenueOnboardingShell
            title="Recover your account"
            description="Enter your email address and we will send password reset instructions when the account is eligible."
        >
            <styled.section borderWidth="1px" borderColor="border.subtle" borderRadius="lg" p={{ base: '5', md: '7' }}>
                <Form method="post">
                    <VStack gap="5" alignItems="stretch">
                        <Field.Root required>
                            <Field.Label>Email</Field.Label>
                            <Input name="email" type="email" autoComplete="email" required />
                        </Field.Root>
                        {actionData?.message && (
                            <styled.p role="status" color="fg.muted">
                                {actionData.message}
                            </styled.p>
                        )}
                        <Button type="submit" width="full" loading={navigation.state === 'submitting'}>
                            Send reset instructions
                        </Button>
                        <styled.a href="/login?intent=venue" color="accent.default" textAlign="center">
                            Return to sign in
                        </styled.a>
                    </VStack>
                </Form>
            </styled.section>
        </VenueOnboardingShell>
    )
}
